import { useCallback, useEffect, useRef, useState } from 'react';
import {
  loadRunningHubHistoryForDisplay,
  type RunningHubHistoryLoadResult,
} from '../utils/runningHubCloudHistory';
import type { RunningHubHistoryFeatureType } from '../utils/runningHubHistoryScope';
import {
  runningHubHistoryItemOwnedBy,
  subscribeRunningHubHistoryReset,
} from '../utils/runningHubHistoryScope';

type ShowToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;

/** 多面板 / 重复 effect 时同一错误只弹一次 Toast */
let lastRhHistoryErrorToastAt = 0;
let lastRhHistoryErrorToastMsg = '';

function showRunningHubHistoryToastOnce(
  showToast: ShowToast,
  message: string,
  type: 'success' | 'error' | 'info' | 'warning' = 'error',
): void {
  const now = Date.now();
  if (message === lastRhHistoryErrorToastMsg && now - lastRhHistoryErrorToastAt < 8000) {
    return;
  }
  lastRhHistoryErrorToastAt = now;
  lastRhHistoryErrorToastMsg = message;
  showToast(message, type);
}

function showRunningHubHistoryErrorToastOnce(showToast: ShowToast, message: string): void {
  showRunningHubHistoryToastOnce(showToast, message, 'error');
}

export type UseRunningHubPanelHistoryOptions<T extends { id: string; timestamp: number }> = {
  featureType: RunningHubHistoryFeatureType;
  isOpen: boolean;
  isAuthenticated: boolean;
  userEmail?: string | null;
  language: 'zh' | 'en';
  showToast: ShowToast;
  setHistory: React.Dispatch<React.SetStateAction<T[]>>;
  cleanExpired: (items: T[]) => T[];
  /** reset / 换号前：清空选中、结果图等 */
  onBeforeClear?: () => void;
  /** 写入 state 前变换（如 tone_mapping persist） */
  mapLoadedItems?: (items: T[], result: RunningHubHistoryLoadResult<T>) => T[];
  /**
   * 云端成功返回空列表时，若内存中仍有本会话已加载的历史，则保留 UI（避免被旧聚合/过滤瞬时清空）。
   * 仅物理重构等明确传入 true 的面板使用。
   */
  keepHistoryOnSuccessfulCloudEmpty?: boolean;
};

/**
 * RunningHub 侧栏历史：换号 / reset 后自动重拉云端；load 带 ownerEmail + requestId 防串号。
 */
export function useRunningHubPanelHistory<T extends { id: string; timestamp: number }>(
  options: UseRunningHubPanelHistoryOptions<T>,
): void {
  const {
    featureType,
    isOpen,
    isAuthenticated,
    userEmail,
    language,
    showToast,
    setHistory,
    cleanExpired,
    onBeforeClear,
    mapLoadedItems,
    keepHistoryOnSuccessfulCloudEmpty = false,
  } = options;

  const [reloadNonce, setReloadNonce] = useState(0);
  const loadGenRef = useRef(0);
  const trustedByOwnerRef = useRef<{ ownerEmail: string; items: T[] } | null>(null);

  const zh = language === 'zh';

  const clearPanelHistory = useCallback(() => {
    onBeforeClear?.();
    setHistory([]);
  }, [onBeforeClear, setHistory]);

  const applyLoaded = useCallback(
    (ownerEmail: string, loaded: RunningHubHistoryLoadResult<T>, gen: number) => {
      if (gen !== loadGenRef.current) return;
      const currentOwner = (userEmail || '').trim().toLowerCase();
      if (!currentOwner || currentOwner !== ownerEmail) return;

      let items = loaded.items;
      if (mapLoadedItems) {
        items = mapLoadedItems(items, loaded);
      }

      if (loaded.source === 'cloud_filtered_empty') {
        const snap = trustedByOwnerRef.current;
        const owned =
          snap?.ownerEmail === ownerEmail
            ? snap.items.filter((it) => runningHubHistoryItemOwnedBy(it, userEmail))
            : [];
        if (owned.length > 0) {
          setHistory(owned);
          showRunningHubHistoryToastOnce(
            showToast,
            zh
              ? '云端记录未通过归属校验，仅临时保留本地缓存'
              : 'Cloud records failed ownership check; showing temporary local cache only.',
            'warning',
          );
        } else {
          trustedByOwnerRef.current = { ownerEmail, items: [] };
          setHistory([]);
        }
        return;
      }

      if (items.length > 0) {
        trustedByOwnerRef.current = { ownerEmail, items };
        setHistory(items);
        return;
      }

      // Q5 修正：items.length === 0 时，严格区分「失败 empty」与「成功 empty」。
      // source='empty' 同时承载「云端成功真无 / guest 真无 / cloud 失败且 cache 无」三种语义，
      // 不能用 source 判失败，必须以 errorCode/error 为唯一失败信号。
      const failed = !!loaded.errorCode || !!loaded.error;

      if (failed) {
        // 失败 → 优先用内存 snapshot 覆盖（memory_stale 语义）
        const snap = trustedByOwnerRef.current;
        if (snap?.ownerEmail === ownerEmail && snap.items.length > 0) {
          setHistory(snap.items);
          showRunningHubHistoryErrorToastOnce(
            showToast,
            zh
              ? '云端同步失败，已保留上次成功加载的历史（可关闭面板后重试）'
              : 'Cloud sync failed; showing last loaded history for this account.',
          );
          return;
        }
        // 失败且无 snapshot → 不清空当前 UI；toast 提示一次。
        if (loaded.error) {
          showRunningHubHistoryErrorToastOnce(
            showToast,
            zh ? `历史同步失败：${loaded.error}（请稍后重试）` : `History sync failed: ${loaded.error}. Retry later.`,
          );
        }
        return;
      }

      // 成功 empty（cloud 200 真无 / guest_local 真无）→ 显式同步 UI 与内存快照为空。
      // 这是 Q5 关键修正：防止旧 trustedByOwnerRef 把已被远端删除的数据回填到 UI。
      if (keepHistoryOnSuccessfulCloudEmpty) {
        const snap = trustedByOwnerRef.current;
        if (snap?.ownerEmail === ownerEmail && snap.items.length > 0) {
          return;
        }
      }
      trustedByOwnerRef.current = { ownerEmail, items: [] };
      setHistory([]);
    },
    [keepHistoryOnSuccessfulCloudEmpty, mapLoadedItems, setHistory, showToast, userEmail, zh],
  );

  const runLoad = useCallback(
    async (reason: string) => {
      if (!isOpen || !isAuthenticated) return;
      const ownerEmail = (userEmail || '').trim().toLowerCase();
      if (!ownerEmail) return;

      const gen = ++loadGenRef.current;
      console.log('[runninghub-panel-history] load start', { featureType, ownerEmail, reason, gen });

      try {
        const loaded = await loadRunningHubHistoryForDisplay(
          featureType,
          true,
          userEmail,
          cleanExpired,
          {
            userEmailForLog: userEmail,
            currentUserEmail: userEmail,
          },
        );

        if (loaded.source === 'cache_stale' && loaded.items.length > 0 && !loaded.runninghubHistorySuspiciousEmpty) {
          showRunningHubHistoryErrorToastOnce(
            showToast,
            zh ? '云端同步失败，仅显示本地缓存（只读）' : 'Showing cached history (cloud sync failed).',
          );
        }

        if (loaded.runninghubHistorySuspiciousEmpty && loaded.items.length > 0) {
          showRunningHubHistoryToastOnce(
            showToast,
            zh
              ? '云端历史暂不可用，已保留上次成功同步的记录'
              : 'Cloud history temporarily unavailable; showing last synced records.',
            'warning',
          );
        }

        if (loaded.error && loaded.items.length === 0 && loaded.source !== 'cache_stale') {
          showRunningHubHistoryErrorToastOnce(
            showToast,
            zh ? `云端历史拉取失败：${loaded.error}` : `Cloud history failed to load: ${loaded.error}`,
          );
        }

        applyLoaded(ownerEmail, loaded, gen);
        console.log('[runninghub-panel-history] load done', {
          featureType,
          ownerEmail,
          reason,
          gen,
          count: loaded.items.length,
          source: loaded.source,
        });
      } catch (e: unknown) {
        if (gen !== loadGenRef.current) return;
        const currentOwner = (userEmail || '').trim().toLowerCase();
        if (currentOwner !== ownerEmail) return;

        const msg = e instanceof Error ? e.message : String(e);
        console.error('[runninghub-panel-history] load exception', { featureType, ownerEmail, reason, msg });
        const snap = trustedByOwnerRef.current;
        if (snap?.ownerEmail === ownerEmail && snap.items.length > 0) {
          setHistory(snap.items);
          showRunningHubHistoryErrorToastOnce(
            showToast,
            zh ? '历史同步失败，已保留上次成功记录' : 'Sync failed; kept last successful history.',
          );
        } else {
          showRunningHubHistoryErrorToastOnce(
            showToast,
            zh ? `历史同步失败：${msg}` : `History sync failed: ${msg}`,
          );
        }
      }
    },
    [
      applyLoaded,
      cleanExpired,
      featureType,
      isAuthenticated,
      isOpen,
      showToast,
      userEmail,
      zh,
    ],
  );

  const runLoadRef = useRef(runLoad);
  runLoadRef.current = runLoad;

  const prevUserEmailRef = useRef<string | null>(null);

  // Step 3：reset 事件不再清空已显示历史；仅作废当前 in-flight 并触发重拉。
  // 真账号切换由下方 userEmail effect 负责 clearPanelHistory（防串号）。
  useEffect(() => {
    return subscribeRunningHubHistoryReset(() => {
      loadGenRef.current += 1;
      setReloadNonce((n) => n + 1);
    });
  }, []);

  useEffect(() => {
    const owner = (userEmail || '').trim().toLowerCase();
    if (prevUserEmailRef.current === owner) return;
    prevUserEmailRef.current = owner;
    loadGenRef.current += 1;
    trustedByOwnerRef.current = null;
    clearPanelHistory();
    setReloadNonce((n) => n + 1);
  }, [userEmail, clearPanelHistory]);

  useEffect(() => {
    if (!isOpen || !isAuthenticated || !(userEmail || '').trim()) return;
    void runLoadRef.current(`open:${reloadNonce}`);
    return () => {
      loadGenRef.current += 1;
    };
  }, [isOpen, isAuthenticated, userEmail, reloadNonce]);
}
